import { useState } from "react";
import {
  DEFAULT_TOPIC_COUNT,
  HOMEWORK_MAX_EXERCISES,
  LESSON_EXERCISE_BATCH_MAX,
  defaultDifficulty,
  findLevel,
  findTopic,
  maxCountFor,
  mergedCount,
  picksTotal,
  rowProblem,
  toTopicPicks,
  type OgeTopic,
  type OgeTopicPick,
  type PickProblem,
  type TopicPickRow,
} from "@edu/shared";
import { newId } from "./new-id.ts";
import type { TopicSubject } from "./subject.ts";
import type { TopicPicksPlace } from "./topic-picks-text.ts";
import { useTopicCatalog } from "./use-topic-catalog.ts";

/**
 * Состояние ветки «По темам»: справочник с сервера и строки-пики (5.5
 * docs/homework-phase2-design.md).
 *
 * Отдельным модулем от формы по той же причине, что и ход раздачи: форма
 * отвечает на «кому и когда», а здесь живёт «сколько по этой теме можно и из
 * чего это соберётся». Правила счёта при этом не здесь, а в `@edu/shared`,
 * рядом со своими тестами: проверять надо именно их, а тестов на хуки нет.
 *
 * ⚠️ Сама загрузка справочника уехала 03.09.2026 в `use-topic-catalog.ts`
 * рядом: её спрашивает и выбор одной темы в комнате урока, где строк-пиков нет
 * вовсе. Здесь осталось всё, что про строки, и поведение формы разрез не
 * изменил ни на шаг.
 *
 * ⚠️ Переехал сюда из `features/homework/assign/` 30.09.2026 (раздел 7.1
 * `docs/room-exercise-batch-design.md`): те же строки стоят у пачки заданий на
 * уроке. Отличие двух экранов одно, МЕСТО, и оно передаётся ОДИН раз, сюда, а
 * дальше едет в состоянии: потолок и слова, зависящие от места, выводятся из
 * него. Второе свойство компонента того же смысла однажды разошлось бы с этим
 * («потолок урока, тексты домашки»).
 *
 * ⚠️ Справочник грузится СРАЗУ, а не по выбору радиокнопки. Причина в состояниях
 * (5.10): пока он не приехал, неизвестно, можно ли вообще предлагать этот
 * источник, а выключенная радиокнопка обязана объяснить, почему она выключена.
 * Ответ кэшируется на час, значит второй заход в форму запроса не делает.
 *
 * ⚠️ Хук ПРЕДМЕТНЫЙ, и форма зовёт его дважды: по разу на математику и на
 * английский (раздел 7, пункт 15 `docs/english-generators-design.md`). Один хук
 * с переключаемым предметом здесь хуже: строки-пики хранят слаги, а слаги у
 * предметов разные, и переключение источника либо унесло бы набранные строки,
 * либо отправило бы английский слаг в математический реестр. Два состояния
 * рядом стоят ровно потому, что переключатель обязан быть обратимым.
 */

/**
 * Потолок заданий по месту. ⚠️ Картой, а не условием: новое место без потолка
 * здесь это ошибка компиляции, а не молчаливые пятьдесят.
 *
 * ⚠️ У урока именно `LESSON_EXERCISE_BATCH_MAX` (двенадцать), а НЕ
 * `LESSON_BATCH_MAX` из `room.ts`: то имя занято потолком пачки уроков
 * (тридцать), и перепутанное молча дало бы форме урока потолок тридцать.
 */
const HARD_CAP: Record<TopicPicksPlace, number> = {
  homework: HOMEWORK_MAX_EXERCISES,
  lesson: LESSON_EXERCISE_BATCH_MAX,
};

export interface TopicPicks {
  /** Чей это справочник. От него зависят слова про уровень материала. */
  subject: TopicSubject;
  /** Где стоят строки: от этого зависят потолок и часть слов. */
  place: TopicPicksPlace;
  /** Потолок заданий всеми строками вместе, выведенный из места. */
  hardCap: number;
  /** `null` означает «ещё грузится»: пустой массив это другое, это «тем нет». */
  topics: OgeTopic[] | null;
  /** Справочник не загрузился. Ветка «Из заготовки» при этом продолжает работать. */
  failed: boolean;
  reload: () => void;
  rows: TopicPickRow[];
  /**
   * Всего заданий по строкам С ВЫБРАННОЙ ТЕМОЙ. Строка без темы на сервер не
   * уезжает, и в счётчике «всего заданий: N из M» её нет: у неё количество
   * равно умолчанию, и «15 из 50» при уезжающих 10 это враньё экрана.
   */
  total: number;
  /** Проблема строки, если она есть: текст рисует форма, у строки. */
  problemOf: (row: TopicPickRow) => PickProblem | null;
  /** Верхняя граница поля количества у этой строки. */
  maxOf: (row: TopicPickRow) => number;
  /**
   * Сколько заданий закажется по теме и уровню этой строки всеми строками
   * вместе: по нему считается предпросмотр состава, потому что сервер сложит
   * строки в один пик (`mergePicks`).
   */
  mergedOf: (row: TopicPickRow) => number;
  /** Всё выбрано и годится: кнопка «Задать» может отпускаться. */
  ready: boolean;
  picks: OgeTopicPick[];
  add: () => void;
  remove: (rowId: string) => void;
  setSkill: (rowId: string, skill: string) => void;
  setDifficulty: (rowId: string, difficulty: number) => void;
  setCount: (rowId: string, count: number) => void;
  /** Восстановление брошенной раздачи по темам: строки из сохранённого заказа. */
  restore: (picks: readonly OgeTopicPick[]) => void;
}

function emptyRow(): TopicPickRow {
  return { rowId: newId(), skill: "", difficulty: null, count: DEFAULT_TOPIC_COUNT };
}

export function useTopicPicks(subject: TopicSubject, place: TopicPicksPlace): TopicPicks {
  /*
    Предмет уезжает в справочник как есть: он у экземпляра хука постоянный, но
    зашей его там, и второй вызов хука однажды спросил бы чужой справочник
  */
  const { topics, failed, reload } = useTopicCatalog(subject);
  const [rows, setRows] = useState<TopicPickRow[]>(() => [emptyRow()]);

  const known: OgeTopic[] = topics ?? [];
  const hardCap = HARD_CAP[place];

  /**
   * Правка одной строки. Пересчёт количества делается ЗДЕСЬ, а не в поле ввода:
   * смена темы или уровня меняет потолок, и оставленное как есть количество
   * превратило бы годную строку в негодную без единого действия человека.
   */
  const patch = (rowId: string, make: (row: TopicPickRow) => TopicPickRow): void => {
    setRows((prev) => {
      const next = prev.map((r) => (r.rowId === rowId ? make(r) : r));
      return next.map((r) => {
        if (r.rowId !== rowId) return r;
        const topic = r.skill === "" ? null : findTopic(known, r.skill);
        const level = topic === null ? null : findLevel(topic, r.difficulty);
        if (level === null) return r;
        const max = maxCountFor(level, next, rowId, hardCap);
        return { ...r, count: Math.max(1, Math.min(r.count, max)) };
      });
    });
  };

  const maxOf = (row: TopicPickRow): number => {
    const topic = row.skill === "" ? null : findTopic(known, row.skill);
    const level = topic === null ? null : findLevel(topic, row.difficulty);
    if (level === null) return hardCap;
    return maxCountFor(level, rows, row.rowId, hardCap);
  };

  const mergedOf = (row: TopicPickRow): number => mergedCount(rows, row);

  const problemOf = (row: TopicPickRow): PickProblem | null =>
    rowProblem(known, rows, row, hardCap);

  const ready =
    topics !== null &&
    rows.length > 0 &&
    rows.every((r) => rowProblem(known, rows, r, hardCap) === null);

  return {
    subject,
    place,
    hardCap,
    topics,
    failed,
    reload,
    rows,
    total: picksTotal(rows),
    problemOf,
    maxOf,
    mergedOf,
    ready,
    picks: toTopicPicks(rows),
    add: () => setRows((prev) => [...prev, emptyRow()]),
    remove: (rowId) =>
      setRows((prev) => {
        const left = prev.filter((r) => r.rowId !== rowId);
        // Последняя строка не удаляется в ноль: пустой блок «Задания» без
        // единого поля читается как поломка, а не как «добавьте тему»
        return left.length === 0 ? [emptyRow()] : left;
      }),
    setSkill: (rowId, skill) =>
      patch(rowId, (r) => {
        const topic = skill === "" ? null : findTopic(known, skill);
        return {
          ...r,
          skill,
          // ⚠️ Умолчание уровня ждёт решения владельца, разбор у
          // `defaultDifficulty` в `@edu/shared`
          difficulty: topic === null ? null : defaultDifficulty(topic),
        };
      }),
    setDifficulty: (rowId, difficulty) => patch(rowId, (r) => ({ ...r, difficulty })),
    setCount: (rowId, count) => patch(rowId, (r) => ({ ...r, count })),
    restore: (picks) =>
      setRows(
        picks.length === 0
          ? [emptyRow()]
          : picks.map((p) => ({
              rowId: newId(),
              skill: p.skill,
              difficulty: p.difficulty ?? null,
              count: p.count,
            })),
      ),
  };
}
